// src/components/MerchSizeSelector.js
// ============================================================================
// MERCH SIZE SELECTOR MODAL
//
// Reusable modal for "Buy Now" on multi-size merch products. Caller passes
// product info + size list. On confirm, navigates to MerchCheckoutScreen
// with the selected variant in route params. MerchCheckoutScreen handles
// the payment method choice (wallet vs Stripe) and the actual order
// submission.
//
// Caller is responsible for:
//   - Showing/hiding the modal (visible prop)
//   - Passing `navigation` so we can route to MerchCheckout
//   - Passing the correct productKey + sizes matching MERCH_CATALOG in
//     functions/payments/stripeMerch.js
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
  Alert,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

const showAlert = (title, message) => {
  if (Platform.OS === 'web') {
    // eslint-disable-next-line no-alert
    alert(`${title}\n\n${message}`);
  } else {
    Alert.alert(title, message);
  }
};

export default function MerchSizeSelector({
  visible,
  onClose,
  navigation,
  productKey,
  productName,
  priceCents,
  sizes,
  // Optional: when set, a second "Add to Cart" button renders alongside the
  // existing "Continue to Checkout" CTA. The caller receives the selected
  // size payload and is expected to call useCart().addItem(...). When null
  // (the default), only the checkout button shows — preserving the
  // existing direct-buy flow.
  onAddToCart,
}) {
  const [selectedSize, setSelectedSize] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Reset selection whenever the modal opens for a different product
  useEffect(() => {
    if (visible) {
      setSelectedSize(null);
      setSubmitting(false);
    }
  }, [visible, productKey]);

  const resolvePicked = () => {
    const obj = sizes.find((s) => s.key === selectedSize);
    return {
      size: selectedSize,
      sizeLabel: obj?.label || selectedSize,
      unitAmountCents:
        typeof obj?.priceCents === 'number' ? obj.priceCents : priceCents,
    };
  };

  const handleConfirm = () => {
    if (!selectedSize) {
      showAlert('Pick a size', 'Choose a size before continuing to checkout.');
      return;
    }
    if (!navigation) {
      showAlert('Navigation missing', 'Cannot route to checkout — please refresh and try again.');
      return;
    }

    const picked = resolvePicked();
    setSubmitting(true);
    onClose && onClose();
    navigation.navigate('MerchCheckout', {
      productKey,
      productName,
      size: picked.size,
      sizeLabel: picked.sizeLabel,
      priceCents: picked.unitAmountCents,
    });
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      showAlert('Pick a size', 'Choose a size before adding to cart.');
      return;
    }
    const picked = resolvePicked();
    onAddToCart && onAddToCart({
      productKey,
      productName,
      size: picked.size,
      sizeLabel: picked.sizeLabel,
      unitAmountCents: picked.unitAmountCents,
    });
    onClose && onClose();
  };

  // If any size has its own priceCents, treat the product as multi-priced
  // (Mouse Pad). The header price shows a "From $X" lower-bound; the size
  // chips append each price; the confirm button uses the selected size's
  // price.
  const formatCents = (cents) => `$${(cents / 100).toFixed(0)}`;
  const sizesWithPrice = sizes.filter((s) => typeof s.priceCents === 'number');
  const hasVariantPricing = sizesWithPrice.length > 0;
  const minVariantCents = hasVariantPricing
    ? Math.min(...sizesWithPrice.map((s) => s.priceCents))
    : priceCents;
  const headerPriceDisplay = hasVariantPricing
    ? `From ${formatCents(minVariantCents)}`
    : formatCents(priceCents);
  const selectedSizeObj = sizes.find((s) => s.key === selectedSize);
  const selectedPriceCents =
    typeof selectedSizeObj?.priceCents === 'number'
      ? selectedSizeObj.priceCents
      : priceCents;
  const selectedPriceDisplay = formatCents(selectedPriceCents);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        if (!submitting) onClose();
      }}
    >
      <View style={styles.backdrop}>
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <View style={{ flex: 1 }}>
              <Text style={styles.modalEyebrow}>SELECT YOUR SIZE</Text>
              <Text style={styles.modalTitle}>{productName}</Text>
              <Text style={styles.modalPrice}>{headerPriceDisplay}</Text>
            </View>
            {!submitting && (
              <TouchableOpacity onPress={onClose} hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                <Ionicons name="close" size={24} color="#999" />
              </TouchableOpacity>
            )}
          </View>

          {/* Size grid */}
          <ScrollView style={styles.sizeListScroll} contentContainerStyle={styles.sizeGrid}>
            {sizes.map((s) => {
              const isSelected = selectedSize === s.key;
              // Render size label with appended price when this product
              // has variant-level pricing (Mouse Pad). For uniform-priced
              // products, the label stays clean.
              const chipText = hasVariantPricing && typeof s.priceCents === 'number'
                ? `${s.label} · ${formatCents(s.priceCents)}`
                : s.label;
              return (
                <TouchableOpacity
                  key={s.key}
                  style={[styles.sizeChip, isSelected && styles.sizeChipSelected]}
                  onPress={() => setSelectedSize(s.key)}
                  disabled={submitting}
                >
                  <Text
                    style={[
                      styles.sizeChipLabel,
                      isSelected && styles.sizeChipLabelSelected,
                    ]}
                  >
                    {chipText}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>

          {/* Add to Cart — only renders when caller wires the callback. */}
          {onAddToCart ? (
            <TouchableOpacity
              style={[
                styles.addCartButton,
                (!selectedSize || submitting) && styles.addCartButtonDisabled,
              ]}
              onPress={handleAddToCart}
              disabled={!selectedSize || submitting}
            >
              <Text style={styles.addCartButtonText}>
                {selectedSize ? `Add to Cart — ${selectedPriceDisplay}` : 'Add to Cart'}
              </Text>
            </TouchableOpacity>
          ) : null}

          {/* Continue to Checkout — direct-buy path, bypasses the cart. */}
          <TouchableOpacity
            style={[
              styles.confirmButton,
              (!selectedSize || submitting) && styles.confirmButtonDisabled,
            ]}
            onPress={handleConfirm}
            disabled={!selectedSize || submitting}
          >
            {submitting ? (
              <ActivityIndicator color="#0A0A0A" />
            ) : (
              <Text style={styles.confirmButtonText}>
                {selectedSize
                  ? (onAddToCart ? `Buy Now — ${selectedPriceDisplay}` : `Continue to Checkout — ${selectedPriceDisplay}`)
                  : 'Select a size'}
              </Text>
            )}
          </TouchableOpacity>

          <Text style={styles.modalFootnote}>
            Secure checkout via Stripe. US-only fulfillment.
          </Text>
        </View>
      </View>
    </Modal>
  );
}

// ============================================================================
// STYLES
// ============================================================================

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 480,
    backgroundColor: '#1A1A1A',
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: '#333',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  modalEyebrow: {
    color: '#00B4D8',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 2,
    marginBottom: 6,
  },
  modalTitle: {
    color: '#FFF',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  modalPrice: {
    color: '#FCD000',
    fontSize: 22,
    fontWeight: '800',
  },
  sizeListScroll: {
    maxHeight: 220,
    marginBottom: 20,
  },
  sizeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  sizeChip: {
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#333',
    backgroundColor: '#0E0E0E',
    minWidth: 60,
    alignItems: 'center',
  },
  sizeChipSelected: {
    borderColor: '#FCD000',
    backgroundColor: '#2A2A0A',
  },
  sizeChipLabel: {
    color: '#CCC',
    fontSize: 15,
    fontWeight: '700',
  },
  sizeChipLabelSelected: {
    color: '#FCD000',
  },
  // Secondary "Add to Cart" — gold-bordered transparent button, sits above
  // the solid primary Buy Now / Checkout button. Visual hierarchy: Buy Now
  // remains the louder CTA (it's the conversion-optimized direct path);
  // Add to Cart is the secondary "I want to keep shopping" path.
  addCartButton: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 10,
    borderWidth: 2,
    borderColor: '#FCD000',
  },
  addCartButtonDisabled: {
    borderColor: '#555',
  },
  addCartButtonText: {
    color: '#FCD000',
    fontSize: 15,
    fontWeight: '800',
  },
  confirmButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
    marginBottom: 12,
  },
  confirmButtonDisabled: {
    backgroundColor: '#555',
  },
  confirmButtonText: {
    color: '#0A0A0A',
    fontSize: 16,
    fontWeight: '800',
  },
  modalFootnote: {
    color: '#666',
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 16,
  },
});
